import { useState, useEffect } from 'react';
import { sharepointAPI } from '../lib/api';
import { FiCloud, FiCheck, FiX, FiLoader, FiTrash2, FiRefreshCw, FiExternalLink, FiAlertCircle } from 'react-icons/fi';
import SharePointBrowser from './SharePointBrowser';

export default function SharePointSettings() {
  const [connections, setConnections] = useState([]);
  const [connectionStats, setConnectionStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [showBrowser, setShowBrowser] = useState(false);
  const [resyncingConnections, setResyncingConnections] = useState({});

  useEffect(() => {
    loadConnections();
    // Check for OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('sharepoint') === 'connected') {
      setSuccess('SharePoint account connected successfully!');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      // Reload connections
      setTimeout(() => loadConnections(), 1000);
    }
    if (urlParams.get('error')) {
      setError(decodeURIComponent(urlParams.get('error')));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadConnections = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await sharepointAPI.getConnections();
      const conns = response.data.connections || [];
      setConnections(conns);

      // Load stats for each connection
      const stats = {};
      for (const conn of conns) {
        try {
          const statusResponse = await sharepointAPI.getSyncStatus(conn.id);
          stats[conn.id] = statusResponse.data;
        } catch (err) {
          console.error(`Failed to load stats for connection ${conn.id}:`, err);
        }
      }
      setConnectionStats(stats);
    } catch (err) {
      console.error('Failed to load SharePoint connections:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError(null);
      const response = await sharepointAPI.getAuthUrl();
      const { authUrl } = response.data;
      // Redirect to Microsoft OAuth
      window.location.href = authUrl;
    } catch (err) {
      console.error('Failed to get auth URL:', err);
      setError(err.response?.data?.error || err.message);
      setConnecting(false);
    }
  };

  const handleDisconnect = async (connectionId) => {
    if (!confirm('Are you sure you want to disconnect this SharePoint account?')) {
      return;
    }

    try {
      setError(null);
      await sharepointAPI.disconnect(connectionId);
      setSuccess('SharePoint account disconnected successfully!');
      setTimeout(() => setSuccess(null), 3000);
      loadConnections();
    } catch (err) {
      console.error('Failed to disconnect:', err);
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleManageConnection = (connection) => {
    setSelectedConnection(connection);
    setShowBrowser(true);
  };

  const handleResync = async (connection) => {
    if (!connection.connected_sites || connection.connected_sites.length === 0) {
      setError('No sites connected. Please use "Manage" to select sites first.');
      return;
    }

    if (!confirm('This will resync all connected sites and update existing document metadata. Continue?')) {
      return;
    }

    try {
      setResyncingConnections({ ...resyncingConnections, [connection.id]: true });
      setError(null);

      await sharepointAPI.startSync(connection.id, connection.connected_sites);

      setSuccess('Resync started successfully! Documents will be updated shortly.');
      setTimeout(() => setSuccess(null), 3000);
      loadConnections();
    } catch (err) {
      console.error('Failed to resync:', err);
      const errorMessage = err.response?.data?.error || err.message;

      // Check if it's an authentication error
      if (errorMessage.toLowerCase().includes('refresh token') ||
          errorMessage.toLowerCase().includes('reconnect') ||
          errorMessage.toLowerCase().includes('expired')) {
        setError('Authentication expired. Please disconnect and reconnect your SharePoint account to continue syncing.');
      } else {
        setError(`Resync failed: ${errorMessage}`);
      }
    } finally {
      setResyncingConnections({ ...resyncingConnections, [connection.id]: false });
    }
  };

  const handleSyncComplete = () => {
    setShowBrowser(false);
    setSelectedConnection(null);
    setSuccess('Sync started successfully! Documents will begin appearing shortly.');
    setTimeout(() => setSuccess(null), 3000);
    loadConnections();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-50';
      case 'expired':
        return 'text-yellow-600 bg-yellow-50';
      case 'disconnected':
        return 'text-gray-600 bg-gray-50';
      case 'error':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <FiCheck className="w-4 h-4" />;
      case 'expired':
      case 'error':
        return <FiAlertCircle className="w-4 h-4" />;
      default:
        return <FiX className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <FiLoader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (showBrowser && selectedConnection) {
    return (
      <SharePointBrowser
        connection={selectedConnection}
        onComplete={handleSyncComplete}
        onCancel={() => {
          setShowBrowser(false);
          setSelectedConnection(null);
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <FiCloud className="w-6 h-6 text-blue-600" />
            SharePoint Integration
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Connect your Microsoft SharePoint or OneDrive accounts to access documents directly from Doxolve
          </p>
        </div>
        <button
          onClick={handleConnect}
          disabled={connecting}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {connecting ? (
            <>
              <FiLoader className="w-4 h-4 mr-2 animate-spin" />
              Connecting...
            </>
          ) : (
            <>
              <FiCloud className="w-4 h-4 mr-2" />
              Connect SharePoint
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <FiAlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
          <FiCheck className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-green-800">Success</h3>
            <p className="text-sm text-green-700 mt-1">{success}</p>
          </div>
        </div>
      )}

      {connections.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <FiCloud className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No SharePoint Connections</h3>
          <p className="text-gray-600 mb-4">
            Connect your SharePoint or OneDrive account to access your documents
          </p>
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <FiCloud className="w-4 h-4 mr-2" />
            Connect Now
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {connections.map((connection) => (
            <div
              key={connection.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <FiCloud className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-medium text-gray-900">
                        {connection.tenant_name || 'SharePoint Account'}
                      </h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(connection.status)}`}>
                        {getStatusIcon(connection.status)}
                        <span className="ml-1 capitalize">{connection.status}</span>
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Tenant ID: {connection.tenant_id}
                    </p>
                    {connection.last_sync_at && (
                      <p className="text-sm text-gray-500">
                        Last synced: {new Date(connection.last_sync_at).toLocaleString()}
                      </p>
                    )}
                    {connectionStats[connection.id] && connectionStats[connection.id].totalFiles > 0 && (
                      <p className="text-sm font-medium text-green-600">
                        {connectionStats[connection.id].totalFiles} file{connectionStats[connection.id].totalFiles !== 1 ? 's' : ''} synced
                      </p>
                    )}
                    {connection.connected_sites && connection.connected_sites.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-700">Connected Sites:</p>
                        <ul className="mt-1 space-y-1">
                          {connection.connected_sites.map((site, idx) => (
                            <li key={idx} className="text-sm text-gray-600 flex items-start">
                              <FiExternalLink className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                              <span className="break-all">
                                {site.site_name} - {site.drive_name}
                                {site.folder_name && (
                                  <span className="text-blue-600 font-medium"> / {site.folder_name}</span>
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {connection.connected_sites && connection.connected_sites.length > 0 && (
                    <button
                      onClick={() => handleResync(connection)}
                      disabled={resyncingConnections[connection.id]}
                      className="inline-flex items-center px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Resync all connected sites and update document metadata"
                    >
                      {resyncingConnections[connection.id] ? (
                        <>
                          <FiLoader className="w-4 h-4 mr-1 animate-spin" />
                          Resyncing...
                        </>
                      ) : (
                        <>
                          <FiRefreshCw className="w-4 h-4 mr-1" />
                          Resync
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => handleManageConnection(connection)}
                    className="inline-flex items-center px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors"
                  >
                    <FiRefreshCw className="w-4 h-4 mr-1" />
                    Manage
                  </button>
                  <button
                    onClick={() => handleDisconnect(connection.id)}
                    className="inline-flex items-center px-3 py-1.5 text-sm bg-red-50 text-red-700 rounded hover:bg-red-100 transition-colors"
                  >
                    <FiTrash2 className="w-4 h-4 mr-1" />
                    Disconnect
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">About SharePoint Integration</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Files remain in SharePoint - only metadata is synced</li>
          <li>• Access documents directly from Doxolve without downloading</li>
          <li>• AI processing and search work seamlessly across all sources</li>
          <li>• Use "Resync" to update document metadata from SharePoint</li>
          <li>• Use "Manage" to browse and select additional sites/libraries</li>
          <li>• Support for multiple SharePoint tenants</li>
        </ul>
      </div>
    </div>
  );
}
